import {
  InfoApi,
  PlatformInfo,
  RequestOptions,
} from '@agilelab/plugin-wb-platform';
import { DiscoveryApi, FetchApi } from '@mainstage/core-plugin-api';
import { handleFailedResponse } from '../error';

export class InfoApiClient implements InfoApi {
  private readonly discoveryApi: DiscoveryApi;
  private readonly fetchApi: FetchApi;

  constructor(options: { discoveryApi: DiscoveryApi; fetchApi: FetchApi }) {
    this.discoveryApi = options.discoveryApi;
    this.fetchApi = options.fetchApi;
  }

  async getInfo(options: RequestOptions = {}): Promise<PlatformInfo> {
    const baseUrl = await this.discoveryApi.getBaseUrl('platform');
    const url = `${baseUrl}/info`;
    const headers: Record<string, string> = {};
    if (options.token) {
      headers.authorization = `Bearer ${options.token}`;
    }
    const response = await this.fetchApi.fetch(url, { headers });
    // 402 = backend detected an invalid or expired license
    if (response.status === 402) {
      return response.json();
    }
    if (!response.ok) {
      throw new Error(`Failed to fetch platform info: ${response.statusText}`);
    }
    return response.json();
  }

  /**
   * Restricts platform active modules to the provided list (cannot include non-licensed modules).
   * This is an undocumented feature and has any effect only if module override feature is enabled in the platform configuration.
   * @param modules The list of modules to set as active
   * @returns A promise that resolves when the operation is complete
   */
  async setModulesOverride(modules: string[]): Promise<void> {
    const baseUrl = await this.discoveryApi.getBaseUrl('platform');
    const url = `${baseUrl}/modules`;
    const response = await this.fetchApi.fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ modules }),
    });
    await handleFailedResponse(response);
    return response.json();
  }
}
